import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { TimeSheet, CwUsersByMemberIdDict, DepartmentsDict, ReportsToDict, UserTypesDict } from '../types/api.types';
import './TimeSheetFilterSort.css';

// --- public types --------------------------------------------------------

export type SortField = 'name' | 'department' | 'manager';
export type SortDirection = 'asc' | 'desc';

export interface FilterSortState {
  filterName: string;
  filterDepartment: string;
  filterManager: string;
  filterType: string;
  sortField: SortField;
  sortDirection: SortDirection;
}

interface TimeSheetFilterSortProps {
  timeSheets: TimeSheet[];
  cwUsersByMemberId: CwUsersByMemberIdDict;
  departmentsDict: DepartmentsDict;
  reportsToDict: ReportsToDict;
  userTypesDict: UserTypesDict;
  onChange: (filtered: TimeSheet[]) => void;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  onToggleAllTimeDetail?: () => void;
  timeDetailShown?: boolean;
}

// --- helpers -------------------------------------------------------------

/** Look up the CwUser for a time-sheet member (user.id == member.id) */
const findUser = (
  sheet: TimeSheet,
  usersById: CwUsersByMemberIdDict
) => {
  const memberId = sheet.member?.id;
  return memberId !== undefined ? usersById[memberId] : undefined;
};

/** Resolve user metadata for a time-sheet's member via the users dictionary */
const resolveUser = (
  sheet: TimeSheet,
  usersById: CwUsersByMemberIdDict,
  departmentsDict: DepartmentsDict,
  reportsToDict: ReportsToDict,
  userTypesDict: UserTypesDict
) => {
  const user = findUser(sheet, usersById);
  const deptId = user?.defaultDepartment?.id;
  const mgrId = user?.reportsTo?.id ?? user?.timeApprover?.id;
  const typeId = user?.type?.id;
  return {
    name: sheet.member?.name || user?.name || '',
    departmentId: deptId,
    department: deptId !== undefined ? (departmentsDict[deptId] ?? '') : '',
    managerId: mgrId,
    manager: mgrId !== undefined ? (reportsToDict[mgrId] ?? '') : '',
    typeId: typeId,
    type: typeId !== undefined ? (userTypesDict[typeId] ?? '') : '',
  };
};

// --- searchable dropdown -------------------------------------------------

interface DropdownOption {
  value: string;
  label: string;
}

interface SearchableDropdownProps {
  id: string;
  label: string;
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  id,
  label,
  options,
  value,
  onChange,
  placeholder = 'Search…',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label || '',
    [options, value]
  );

  const filtered = useMemo(() => {
    if (!search) return options;
    const term = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(term));
  }, [options, search]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = useCallback(
    (val: string) => {
      onChange(val);
      setIsOpen(false);
      setSearch('');
    },
    [onChange]
  );

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
    setSearch('');
    // Focus the search input after opening
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div className="ts-fs-field" ref={containerRef}>
      <label htmlFor={id}>{label}</label>
      <button
        id={id}
        type="button"
        className={`searchable-dd-toggle${value ? ' has-value' : ''}`}
        onClick={handleToggle}
      >
        <span className="searchable-dd-text">{value ? selectedLabel : 'All'}</span>
        <span className="searchable-dd-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && (
        <div className="searchable-dd-popup">
          <input
            ref={inputRef}
            type="text"
            className="searchable-dd-search"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ul className="searchable-dd-list">
            <li
              className={`searchable-dd-item${!value ? ' selected' : ''}`}
              onClick={() => handleSelect('')}
            >
              All
            </li>
            {filtered.map((o) => (
              <li
                key={o.value}
                className={`searchable-dd-item${o.value === value ? ' selected' : ''}`}
                onClick={() => handleSelect(o.value)}
              >
                {o.label}
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="searchable-dd-empty">No matches</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// --- component -----------------------------------------------------------

const TimeSheetFilterSort: React.FC<TimeSheetFilterSortProps> = ({
  timeSheets,
  cwUsersByMemberId,
  departmentsDict,
  reportsToDict,
  userTypesDict,
  onChange,
  onExpandAll,
  onCollapseAll,
  onToggleAllTimeDetail,
  timeDetailShown,
}) => {
  const [state, setState] = useState<FilterSortState>({
    filterName: '',
    filterDepartment: '',
    filterManager: '',
    filterType: '',
    sortField: 'name',
    sortDirection: 'asc',
  });

  // Build unique option lists — departments & managers come from the
  // pre-built dictionaries so every known value appears even if not on
  // a currently-loaded time sheet.
  const options = useMemo(() => {
    const names = new Set<string>();

    timeSheets.forEach((sheet) => {
      const info = resolveUser(sheet, cwUsersByMemberId, departmentsDict, reportsToDict, userTypesDict);
      if (info.name) names.add(info.name);
    });

    const sortStrings = (arr: string[]) => [...arr].sort((a, b) => a.localeCompare(b));

    // Build id/name pairs sorted by name for department & manager dropdowns
    const deptEntries = Object.entries(departmentsDict)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const mgrEntries = Object.entries(reportsToDict)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const typeEntries = Object.entries(userTypesDict)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      names: sortStrings(Array.from(names)),
      departments: deptEntries,
      managers: mgrEntries,
      types: typeEntries,
    };
  }, [timeSheets, cwUsersByMemberId, departmentsDict, reportsToDict, userTypesDict]);

  // Derive the filtered + sorted list whenever inputs change
  const filteredSorted = useMemo(() => {
    let result = timeSheets.filter((sheet) => {
      const info = resolveUser(sheet, cwUsersByMemberId, departmentsDict, reportsToDict, userTypesDict);

      // Dropdown filters — department & manager compare by id
      if (state.filterName && info.name !== state.filterName) return false;
      if (state.filterDepartment && String(info.departmentId) !== state.filterDepartment) return false;
      if (state.filterManager && String(info.managerId) !== state.filterManager) return false;
      if (state.filterType && String(info.typeId) !== state.filterType) return false;

      return true;
    });

    // Sort
    result = [...result].sort((a, b) => {
      const aInfo = resolveUser(a, cwUsersByMemberId, departmentsDict, reportsToDict, userTypesDict);
      const bInfo = resolveUser(b, cwUsersByMemberId, departmentsDict, reportsToDict, userTypesDict);

      const aVal = aInfo[state.sortField].toLowerCase();
      const bVal = bInfo[state.sortField].toLowerCase();

      const cmp = aVal.localeCompare(bVal);
      return state.sortDirection === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [timeSheets, cwUsersByMemberId, departmentsDict, reportsToDict, userTypesDict, state]);

  // Notify parent whenever the derived list changes
  const prevRef = useRef(filteredSorted);
  useEffect(() => {
    if (prevRef.current !== filteredSorted) {
      prevRef.current = filteredSorted;
      onChange(filteredSorted);
    }
  }, [filteredSorted, onChange]);

  // Emit once on mount so parent gets the initial (unfiltered) order
  useEffect(() => {
    onChange(filteredSorted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- event helpers ---

  const set = <K extends keyof FilterSortState>(key: K, value: FilterSortState[K]) =>
    setState((prev) => ({ ...prev, [key]: value }));

  const toggleDirection = () =>
    setState((prev) => ({
      ...prev,
      sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc',
    }));

  const clearAll = () =>
    setState({
      filterName: '',
      filterDepartment: '',
      filterManager: '',
      filterType: '',
      sortField: 'name',
      sortDirection: 'asc',
    });

  const nameOptions: DropdownOption[] = useMemo(
    () => options.names.map((n) => ({ value: n, label: n })),
    [options.names]
  );
  const deptOptions: DropdownOption[] = useMemo(
    () => options.departments.map((d) => ({ value: d.id, label: d.name })),
    [options.departments]
  );
  const mgrOptions: DropdownOption[] = useMemo(
    () => options.managers.map((m) => ({ value: m.id, label: m.name })),
    [options.managers]
  );
  const typeOptions: DropdownOption[] = useMemo(
    () => options.types.map((t) => ({ value: t.id, label: t.name })),
    [options.types]
  );

  const hasActiveFilters =
    state.filterName || state.filterDepartment || state.filterManager || state.filterType;

  return (
    <div className="ts-filter-sort">
      {/* Filters + Sort */}
      <div className="ts-fs-row">
        <SearchableDropdown
          id="ts-filter-name"
          label="Name"
          options={nameOptions}
          value={state.filterName}
          onChange={(v) => set('filterName', v)}
          placeholder="Search names…"
        />

        <SearchableDropdown
          id="ts-filter-dept"
          label="Department"
          options={deptOptions}
          value={state.filterDepartment}
          onChange={(v) => set('filterDepartment', v)}
          placeholder="Search departments…"
        />

        <SearchableDropdown
          id="ts-filter-mgr"
          label="Manager"
          options={mgrOptions}
          value={state.filterManager}
          onChange={(v) => set('filterManager', v)}
          placeholder="Search managers…"
        />

        <SearchableDropdown
          id="ts-filter-type"
          label="Type"
          options={typeOptions}
          value={state.filterType}
          onChange={(v) => set('filterType', v)}
          placeholder="Search types…"
        />

        <div className="ts-fs-field ts-fs-sort">
          <label htmlFor="ts-sort-field">Sort by</label>
          <div className="ts-fs-sort-controls">
            <select
              id="ts-sort-field"
              value={state.sortField}
              onChange={(e) => set('sortField', e.target.value as SortField)}
            >
              <option value="name">Name</option>
              <option value="department">Department</option>
              <option value="manager">Manager</option>
            </select>
            <button
              className="ts-fs-dir-btn"
              onClick={toggleDirection}
              title={state.sortDirection === 'asc' ? 'Ascending' : 'Descending'}
              type="button"
            >
              {state.sortDirection === 'asc' ? '▲' : '▼'}
            </button>
          </div>
        </div>

        {hasActiveFilters && (
          <button className="ts-fs-clear-btn" onClick={clearAll} type="button">
            Clear filters
          </button>
        )}
      </div>

      <div className="ts-fs-summary">
        Showing {filteredSorted.length} of {timeSheets.length} time sheet
        {timeSheets.length !== 1 ? 's' : ''}
        <span className="ts-fs-expand-controls">
          <button
            className="ts-fs-expand-btn"
            onClick={onExpandAll}
            type="button"
            title="Expand all time sheets"
          >
            Expand All
          </button>
          <button
            className="ts-fs-expand-btn"
            onClick={onCollapseAll}
            type="button"
            title="Collapse all time sheets"
          >
            Collapse All
          </button>
          <button
            className={`ts-fs-expand-btn${timeDetailShown ? ' ts-fs-toggle-active' : ''}`}
            onClick={onToggleAllTimeDetail}
            type="button"
            title={timeDetailShown ? 'Hide all time details' : 'Show all time details'}
          >
            {timeDetailShown ? '⏱ Hide Times' : '⏱ Show Times'}
          </button>
        </span>
      </div>
    </div>
  );
};

export default TimeSheetFilterSort;
